/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from "react";
import { ethers } from "ethers";
import { motion } from "framer-motion";
import { Wallet, Coins, ArrowRightCircle, RotateCw } from "lucide-react";

import { staticProvider, TOKENS } from "../../config/configContracts";
import { getTokenBalance } from "../../utils/getTokenBalance";
import { UserBalancesProps } from "../../types/types";

const UserBalances: React.FC<UserBalancesProps> = ({
  userAddress,
  refresh,
  onBalanceUpdate,
}) => {
  const [balances, setBalances] = useState({
    ETH: "0",
    USDC: "0",
    NAU: "0",
    NAUY: "0",
    NAUN: "0",
  });
  const [loading, setLoading] = useState(false);

  const fetchBalances = useCallback(async () => {
    if (!userAddress) return;
    setLoading(true);

    try {
      // 1) ETH
      const ethRaw = await staticProvider.getBalance(userAddress);
      const ETH = ethers.formatEther(ethRaw);

      // 2) USDC
      const erc20Abi = [
        "function balanceOf(address) view returns (uint256)",
        "function decimals() view returns (uint8)",
      ];
      const usdcContract = new ethers.Contract(
        TOKENS.USDC,
        erc20Abi,
        staticProvider
      );
      const usdcRaw = await usdcContract.balanceOf(userAddress);
      const usdcDecimals = await usdcContract.decimals();
      const USDC = ethers.formatUnits(usdcRaw, usdcDecimals);

      const [NAU, NAUY, NAUN] = await Promise.all([
        getTokenBalance(TOKENS.NAU, userAddress),
        getTokenBalance(TOKENS.NAUY, userAddress),
        getTokenBalance(TOKENS.NAUN, userAddress),
      ]);

      console.log("Fetched balances:", {
        NAU,
        NAUY,
        NAUN,
      });

      const updated = { ETH, USDC, NAU, NAUY, NAUN };
      setBalances(updated);

      if (onBalanceUpdate) {
        onBalanceUpdate(NAU);
      }
    } catch (err) {
      console.error("Error fetching balances:", err);
    } finally {
      setLoading(false);
    }
  }, [userAddress, refresh]);

  useEffect(() => {
    if (!userAddress) {
      setBalances({ ETH: "0", USDC: "0", NAU: "0", NAUY: "0", NAUN: "0" });
      return;
    }
    fetchBalances();
  }, [userAddress, refresh, fetchBalances]);

  if (!userAddress) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#021b36] to-[#032c58] backdrop-blur-lg p-2 shadow-xl border border-white/10 text-white text-base"
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-bold text-[10px] uppercase text-red-400 tracking-wider">
          WALLET BALANCES – BASE L2
        </h2>
        <button
          onClick={fetchBalances}
          disabled={loading}
          className="p-1 rounded hover:bg-white/10 transition"
          title="Refresh balances"
        >
          <RotateCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading ? (
        <p className="text-center text-sm animate-pulse">
          ⏳ Fetching balances...
        </p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {/* ETH */}
          <li className="flex justify-between items-center bg-[#0d1b2a] px-2 py-1 rounded">
            <span className="flex items-center gap-1 tracking-widest">
              <Wallet size={12} /> ETH
            </span>
            <span className="font-bold text-[16px]">
              {Number(balances.ETH).toFixed(4)}
            </span>
          </li>

          {/* USDC */}
          <li className="flex justify-between items-center bg-[#1b263b] px-2 py-1 rounded">
            <span className="flex items-center gap-1">
              <Coins size={12} /> USDC
            </span>
            <span className=" font-bold text-[16px] tracking-widest">
              {Number(balances.USDC).toFixed(4)}
            </span>
          </li>

          {/* NAU  */}
          <li className="flex justify-between items-center bg-[#ff6666]/20 px-2 py-1 rounded">
            <span className="flex items-center gap-1">
              <ArrowRightCircle size={12} /> NAU
            </span>
            <span className=" font-bold text-[16px] tracking-widest">
              {Number(balances.NAU).toFixed(4)}
            </span>
          </li>

          {/* NAUY  */}
          <li className="flex justify-between items-center bg-[#2e4b80]/20 px-2 py-1 rounded">
            <span className="flex items-center gap-1">
              <ArrowRightCircle size={14} /> NAUY
            </span>
            <span className=" font-bold text-[16px] tracking-widest">
              {Number(balances.NAUY).toFixed(4)}
            </span>
          </li>

          {/* NAUN  */}
          <li className="flex justify-between items-center bg-[#b30202]/20 px-2 py-1 rounded">
            <span className="flex items-center gap-1">
              <ArrowRightCircle size={14} /> NAUN
            </span>
            <span className=" font-bold text-[16px] tracking-widest">
              {Number(balances.NAUN).toFixed(4)}
            </span>
          </li>
        </ul>
      )}

      <p className="text-[12px] text-gray-400 mt-4 text-justify">
        <strong>Note:</strong> Displayed balances include project tokens (NAU,
        NAUY, NAUN) and native Base assets (ETH, USDC). Other Base Mainnet
        tokens held in your wallet are not shown here. For a complete view,
        please check your connected EOA or smart wallet.
      </p>
    </motion.div>
  );
};

export default UserBalances;
